import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const SlideContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 1rem;
  padding-top: 4.5rem;
  max-width: 1200px;
  margin: 0 auto;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 0.8rem;
    padding-top: 4rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    padding-top: 3.5rem;
  }
`;

const SlideTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 2rem;
  background: linear-gradient(45deg, #6F1A3F, #C82B3F);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 1024px) {
    font-size: 3rem;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  width: 100%;
  align-items: start;

  @media (max-width: 1024px) {
    gap: 2.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: 480px) {
    gap: 1.5rem;
  }
`;

const MissionVision = styled.div`
  background: rgba(111, 26, 63, 0.1);
  border: 1px solid rgba(111, 26, 63, 0.3);
  border-radius: 15px;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.2rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: #C82B3F;
  margin-bottom: 1.5rem;
  text-align: center;

  @media (max-width: 1024px) {
    font-size: 1.6rem;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const SectionText = styled.p`
  font-size: 1.2rem;
  color: #ccc;
  line-height: 1.6;
  text-align: center;
  font-style: italic;

  @media (max-width: 1024px) {
    font-size: 1.1rem;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const ValueItem = styled(motion.div)`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(111, 26, 63, 0.2);
  padding: 2rem;
  border-radius: 15px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(200, 43, 63, 0.5);
    transform: translateY(-5px);
    background: rgba(111, 26, 63, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.2rem;
  }
`;

const ValueIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #6F1A3F, #C82B3F);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 1024px) {
    font-size: 2.2rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 0.6rem;
  }
`;

const ValueTitle = styled.h4`
  font-size: 1.3rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.8rem;

  @media (max-width: 1024px) {
    font-size: 1.2rem;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.6rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
`;

const ValueDescription = styled.p`
  font-size: 1rem;
  color: #ccc;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const Slide3 = () => {
  const values = [
    {
      icon: '🚀',
      title: 'Innovation & Excellence',
      description: 'Cutting-edge solutions that push the boundaries of technology'
    },
    {
      icon: '🤝',
      title: 'Client-Centric Collaboration',
      description: 'Working closely with clients to understand their unique needs'
    },
    {
      icon: '📈',
      title: 'Impact-Focused Results',
      description: 'Delivering measurable outcomes that drive business growth'
    },
    {
      icon: '🔗',
      title: 'Seamless Integration',
      description: 'Smooth implementation and integration of all solutions'
    }
  ];

  return (
    <SlideContainer>
      <SlideTitle
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        Mission & Vision
      </SlideTitle>

      <ContentGrid>
        <div>
          <MissionVision
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <SectionTitle>Our Mission</SectionTitle>
            <SectionText>
              "To empower organizations by delivering innovative digital solutions and strategic marketing excellence that drive growth, resilience, and market success in the rapidly evolving digital landscape."
            </SectionText>
          </MissionVision>

          <MissionVision
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{ marginTop: '2rem' }}
          >
            <SectionTitle>Our Vision</SectionTitle>
            <SectionText>
              "To be global leaders in comprehensive digital transformation, crafting cutting-edge technology solutions and transformative marketing experiences that shape the future of business innovation and customer engagement."
            </SectionText>
          </MissionVision>
        </div>

        <div>
          <ValuesGrid>
            {values.map((value, index) => (
              <ValueItem
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <ValueIcon>{value.icon}</ValueIcon>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueItem>
            ))}
          </ValuesGrid>
        </div>
      </ContentGrid>
    </SlideContainer>
  );
};

export default Slide3;
